import instance from '../xhrService';
import { env } from '../../config';
import db, { offlineImageCache } from '../../db/db';
import { withOfflineFallback } from '../offlineFallback';
const uploadBaseUrl = '/api/uploads';

export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    // If file is already a string, just resolve immediately.
    if (typeof file === 'string') {
      resolve(file);

      return;
    }
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

/**
 * Rebuild an uploadable `File` from an offline `images` record.
 *
 * The raw `File` those records keep under `sync.payload` cannot be trusted once
 * the page has reloaded: iOS persists a File as a *reference* to a file in
 * Safari's temp area, and after iOS reclaims that file the record still
 * deserializes into a File object whose bytes are gone. WebKit then puts the
 * multipart request on the wire with an empty body (Content-Length: 0) and the
 * API rejects it with 400 "No file attached". The Base64 copy stored alongside
 * it is real data held in IndexedDB, so rebuild the File from that instead.
 *
 * @param {{file?: string, name?: string, type?: string}} image an `images` row
 * @returns {File | null} null when the row carries no readable Base64 data
 */
/** The Base64 part of an `images` row's `file` (a data URL or bare Base64), or null. */
const base64Of = (image) => {
  const data = image && image.file;

  if (typeof data !== 'string' || !data) {
    return null;
  }

  const comma = data.indexOf(',');

  return comma === -1 ? data : data.slice(comma + 1);
};

/**
 * Whether `fileFromOfflineImage` can rebuild a file from this row, without decoding it: the
 * offline sync asks this of every queued photo, and decoding them all would copy each one.
 * Well-formed Base64 is checked by pattern; anything else (whitespace, odd padding) falls back to
 * decoding, as the rebuild would. SW2-2459 (PR #150 review): a non-empty but unreadable `file` —
 * a data URL with nothing after the comma, or malformed Base64 — is skipped by the upload, so it
 * must not count as sendable either, or it holds the inspection's signature forever.
 *
 * @param {{file?: string}} image an `images` row
 * @returns {boolean}
 */
export const hasReadableImageData = (image) => {
  const base64 = base64Of(image);

  if (!base64) return false;

  const match = /^[A-Za-z0-9+/]+(={0,2})$/.exec(base64);

  if (match) {
    return match[1] ? base64.length % 4 === 0 : base64.length % 4 !== 1;
  }

  try {
    return atob(base64).length > 0;
  } catch (err) {
    return false;
  }
};

export const fileFromOfflineImage = (image) => {
  const base64 = base64Of(image);

  if (!base64) {
    return null;
  }

  const data = image.file;
  const comma = data.indexOf(',');
  const declaredType =
    comma === -1 ? null : (/^data:([^;,]+)/.exec(data) || [])[1];
  const type = image.type || declaredType || 'application/octet-stream';

  let binary;

  try {
    binary = atob(base64);
  } catch (err) {
    console.error('Offline image could not be decoded', image.guid, err);

    return null;
  }

  if (!binary.length) {
    return null;
  }

  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new File([bytes], image.name || 'upload', { type });
};

/**
 * @param {boolean} [omitXfdf] Whether or not to omit the user-generated annotations. Only supported for PDF file types.
 */
const getDownloadUrl = (
  guid,
  size = null,
  omitXfdf = false,
  date = Date.now(),
  projectId = null,
  storageLocation = '',
  version = true,
) => {
  const host = env.REACT_APP_API_BASE_URL;

  let params = [];

  if (version) {
    params.push(`version=${date}`);
  }

  if (size) {
    params.push(`size=${size}`);
  }

  if (!navigator.onLine) {
    params = [];
  }

  if (omitXfdf) {
    params.push(`omitXfdf=${omitXfdf}`);
  }

  if (projectId) {
    params.push(`projectId=${projectId}`);
  }

  if (storageLocation) {
    if (storageLocation === 'sitePhotos') {
      params.push(`storageLocation=${storageLocation}`);
    }
  }

  if (!navigator.onLine) {
    const offlineSrc = offlineImageCache[guid];

    if (offlineSrc) {
      return offlineSrc; // Synchronously return the Base64 image string.
    }

    return null; // Or return a placeholder URL.
  }

  return `${host}/dl/${guid}?${params.join('&')}`;
};
const getGroupUploadUrl = (a, b) =>
  `/api/groups/${a}/uploads` + (b ? `/${b}` : '');
const getControlUploadUrl = (a, b) =>
  `/api/legend-items/${a}/uploads` + (b ? `/${b}` : '');
const getProjectUploadUrl = (a, b) =>
  `/api/projects/${a}/uploads` + (b ? `/${b}` : '');
const getInspectionUploadUrl = (a, b) =>
  `/api/inspections/${a}/uploads` + (b ? `/${b}` : '');
const getProjectArchiveUrl = (a, b) =>
  `/api/projects/archive/${a}/uploads` + (b ? `/${b}` : '');
const getFindingUploadUrl = (a, b) =>
  `/api/findings/${a}/uploads` + (b ? `/${b}` : '');
const getDocumentSortUrl = (modelName) => `/api/documents/${modelName}/sort`;

/**
 * @todo add other document APIs
 */
const uploadApi = {
  /**
   * @param {File} file
   * @param {string | null} [name]
   * @param {{lat: number, lon: number, timestamp?: string | null} | null} [gps]
   */
  create(file, name = null, gps = null) {
    const formData = new FormData();

    if (gps) {
      formData.append('lat', gps.lat);
      formData.append('lon', gps.lon);
      if (gps.timestamp) formData.append('timestamp', gps.timestamp);
    }

    formData.append('file', file, name || file.name);

    // SW2-2321: this was an empty `if (!navigator.onLine) {}` block, so offline
    // the function returned `undefined` and every caller then read `.id` off it
    // — a TypeError, or an association request sent to `/uploads/undefined`.
    // There is no offline queue for a bare upload (offline inspections and
    // findings stash their images in `db.images` instead), so the honest
    // behaviour is to reject and let the caller's error handling run.
    if (!navigator.onLine) {
      return Promise.reject(new Error('Cannot upload a file while offline'));
    } else {
      return instance
        .post(uploadBaseUrl, formData)
        .then((res) => res.data)
        .catch(function (err) {
          console.error('upload failure!!', err);

          // Rethrow: returning the error made it look like a successful upload
          // record to every caller, so `response.id` came back undefined and the
          // follow-up association request was sent to `/uploads/undefined`.
          throw err;
        });
    }
  },
  replace(uploadGuid, file) {
    const formData = new FormData();

    formData.append('file', file, file.name);

    return instance
      .put(`${uploadBaseUrl}/${uploadGuid}`, formData)
      .then((res) => res.data)
      .catch(function (err) {
        console.log('upload (replace) failure!!', err);

        return err;
      });
  },
  rename(uploadGuid, fileName, docType) {
    return instance
      .put(`${uploadBaseUrl}/${uploadGuid}/name`, {
        name: fileName,
        docType: docType,
      })
      .then((res) => res.data)
      .catch((err) => console.error(err));
  },
  move(
    projectId,
    sourceFolderId,
    destinationFolderId,
    destinationSubFolderId,
    destinationAsSubFolderCheck,
    uploadIds,
  ) {
    return instance
      .put(`api/projects/${projectId}/uploads/move`, {
        sourceFolderId: sourceFolderId,
        destinationFolderId: destinationFolderId,
        destinationSubFolderId: destinationSubFolderId,
        destinationAsSubFolderCheck: destinationAsSubFolderCheck,
        uploadIds: [...uploadIds],
      })
      .then((res) => res.data)
      .catch((err) => console.error(err));
  },
  group: {
    index(groupId) {
      return instance
        .get(getGroupUploadUrl(groupId))
        .then((response) => response.data);
    },

    create(groupId, uploadId, scope, regulationSubFolderId = null) {
      return instance
        .post(getGroupUploadUrl(groupId, uploadId), {
          body: scope,
          regulation_sub_folder_id: regulationSubFolderId || null,
        })
        .then((response) => response.data);
    },

    destroy(groupId, uploadId) {
      return instance
        .delete(getGroupUploadUrl(groupId, uploadId))
        .then((response) => response.data);
    },
    async sort({ id, order, documentGroupId, groupGUID }) {
      return await instance.put(getDocumentSortUrl('group'), {
        id,
        order,
        documentGroupId,
      });
    },
    sortDocs(groupId, data, selectedSubFolder = null) {
      return instance
        .put(`/api/groups/${groupId}/uploads/sort`, { data, selectedSubFolder })
        .then((response) => response.data);
    },
  },
  /** (global) legend Item regulations */
  groupControl: {
    indexByGroup(groupId, LegendItemIds = null, projectId) {
      const params = {};

      if (Array.isArray(LegendItemIds)) {
        params.LegendItemIds = LegendItemIds;
      }

      if (projectId) {
        params.projectId = projectId;
      }

      return instance
        .get(`/api/legend-items/group/${groupId}/uploads`, { params })
        .then((response) => response.data);
    },

    create(groupId, controlId, uploadId, projectId) {
      return instance
        .post(getControlUploadUrl(controlId, uploadId), {
          document_group_id: groupId,
          project_id: projectId,
        })
        .then(() => uploadId);
    },

    destroy(groupId, controlId, uploadId) {
      return instance
        .delete(getControlUploadUrl(controlId, uploadId), {
          params: { document_group_id: groupId },
        })
        .then(() => true);
    },

    async sort({ id, order, legendItemId, documentGroupId }) {
      await instance.put(getDocumentSortUrl('legend'), {
        id,
        order,
        legendItemId,
        documentGroupId,
      });

      // const response = await uploadApi.groupControl.indexByGroup(
      //   documentGroupId,
      // );
      // return response.data;
    },
  },
  finding: {
    /**
     * create/update/delete finding images through single endpoint
     * @param {number} findingId
     * @param {{file: File, name: string, annotations?: any[]}[]} newFiles
     * @param {any[]} updatedFiles
     * @param {(fraction: number) => void} [onProgress] SW2-2458: upload progress, 0–1
     * @param {{ background?: boolean }} [options] SW2-2458: a background-queue upload, which
     *   foreground requests do not wait for (see foregroundRequests)
     */
    update(
      findingId,
      newFiles,
      updatedFiles = [],
      photoType = 'initial',
      onProgress,
      { background = false } = {},
    ) {
      const formData = new FormData();

      formData.set('files[]', '');
      formData.append('photo_type', photoType);

      // attach new uploads
      newFiles.forEach((newFile) => {
        const {
          file,
          name,
          annotations = [],
          rotation = 0,
          gpsData,
          timestamp,
        } = newFile;

        formData.append(`files[]`, file, name || file.name);
        formData.append(`annotations[]`, JSON.stringify(annotations || {}));
        formData.append(`rotations[]`, rotation);
        // SW2-2449: one entry per file, even when empty. The API reads these arrays by
        // file position, so skipping photos without GPS shifted every later photo's
        // GPS/date onto the wrong file. A null timestamp must be '' — FormData turns
        // null into the string "null", which the API used to store as 'Invalid date'.
        formData.append(`gpsData[]`, gpsData ? JSON.stringify(gpsData) : '');
        formData.append(`timestamp[]`, timestamp || '');
      });

      updatedFiles.forEach((upload) => {
        formData.append(`uploads[]`, JSON.stringify(upload));
      });

      const config = {};

      if (onProgress) {
        config.onUploadProgress = (e) => {
          if (e.total) onProgress(e.loaded / e.total);
        };
      }
      if (background) config.backgroundUpload = true;

      return instance
        .post(getFindingUploadUrl(findingId), formData, config)
        .then((res) => res.data);
    },
  },
  project: {
    create(
      uploadId,
      projectId,
      projectDocTypeId,
      project_sub_doc_type_id,
      extra,
    ) {
      return instance
        .post(getProjectUploadUrl(projectId, uploadId), {
          project_doc_type_id: projectDocTypeId,
          project_sub_doc_type_id: project_sub_doc_type_id
            ? project_sub_doc_type_id
            : null,
          ...(extra || {}),
        })
        .then((response) => response.data);
    },

    index(projectId, projectDocTypeId = undefined) {
      return instance
        .get(getProjectUploadUrl(projectId), {
          params: {
            project_doc_type_id: projectDocTypeId,
          },
        })
        .then((response) => response.data);
    },

    destroy(projectId, uploadId) {
      return instance
        .delete(getProjectUploadUrl(projectId, uploadId))
        .then((response) => response);
    },

    archive(groupId, uploadId, action) {
      return instance
        .put(getProjectArchiveUrl(groupId, uploadId), {
          action,
        })
        .then((response) => response.data);
    },

    async sort({ id, order, projectId, projectDocTypeId }) {
      await instance.put(getDocumentSortUrl('project'), {
        id,
        order,
        projectId,
        projectDocTypeId,
      });
      const response = await uploadApi.project.index(projectId);

      return response.data;
    },

    replaceDocData(projectId, data) {
      return instance
        .put(`/api/projects/${projectId}/uploads/replaceDocData`, data)
        .then((response) => response.data);
    },

    checkAspectRatioChange(projectId, data) {
      return instance
        .get(`/api/projects/${projectId}/uploads/checkAspectRatioChange`, {
          params: data,
        })
        .then((response) => response.data);
    },
  },
  inspection: {
    create(uploadId, inspectionId) {
      return instance
        .post(getInspectionUploadUrl(inspectionId, uploadId))
        .then((response) => response.data);
    },
    replace(inspectionId, payload) {
      return instance
        .put(getInspectionUploadUrl(inspectionId), payload)
        .then((response) => response.data);
    },
    destroy(uploadId, inspectionId) {
      return instance
        .delete(getInspectionUploadUrl(inspectionId, uploadId))
        .then((response) => response);
    },
  },
  // read-only, one record per (project_upload, inspection)
  projectInspection: {
    async index(projectId, inspectionId, docType) {
      const params = {};

      if (docType) {
        params.project_doc_type_id = docType;
      }

      // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
      // request was attempted and the cached project-inspection uploads were
      // never consulted, so the documents list came back empty.
      return withOfflineFallback(
        () =>
          instance
            .get(
              `/api/projects/${projectId}/inspections/${inspectionId}/uploads`,
              {
                params,
              },
            )
            .then((response) => response.data),
        async () => {
          const projectInspectionUploads = await db.projectInspectionUploads
            .where('project_id')
            .equals(Number(projectId))
            .and((item) => item.inspection_id === Number(inspectionId))
            .toArray();

          // Returning null (not []) on a miss keeps the request error visible
          // instead of reporting "no documents" as a success.
          return projectInspectionUploads.length > 0
            ? projectInspectionUploads[0].data
            : null;
        },
      );
    },
  },

  docTypes(name, projectId) {
    const params = {};

    if (name) {
      params.name = name;
    }

    if (projectId) {
      params.projectId = projectId;
    }

    return instance
      .get('/api/projects/docTypes', { params })
      .then(({ data }) => {
        if (name && data.length === 1) {
          return data[0];
        }

        return data.filter((ele) => !ele.is_custom_type);
      });
  },
  getDimensions(guid) {
    /** @todo use the JSON api (uploadApi.get) for this meta */
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.addEventListener('load', function () {
        return resolve([this.naturalWidth, this.naturalHeight]);
      });

      img.addEventListener('error', function () {
        return reject('cannot load image');
      });

      img.src = getDownloadUrl(guid);
    });
  },
  getDownloadUrl,

  subFolder: {
    add(projectId, projectDocTypeId, newSubDocTypeName) {
      return instance
        .post(
          `${uploadBaseUrl}/projectDocType/${projectDocTypeId}/createSubDocType`,
          { newSubDocTypeName: newSubDocTypeName, projectId: projectId },
        )
        .then((response) => response.data);
    },
    delete(projectDocTypeId, selectedSubDocType) {
      return instance
        .post(
          `${uploadBaseUrl}/projectDocType/${projectDocTypeId}/deleteSubDocType`,
          { selectedSubDocType: selectedSubDocType },
        )
        .then((response) => response.data);
    },
    rename(projectDocTypeId, selectedSubDocType, updatedName) {
      return instance
        .post(
          `${uploadBaseUrl}/projectDocType/${projectDocTypeId}/renameSubDocType`,
          { selectedSubDocType: selectedSubDocType, updatedName: updatedName },
        )
        .then((response) => response.data);
    },
  },

  /** Subfolders inside a master Regulation Group */
  groupSubFolder: {
    add(groupId, name, projectId = null) {
      return instance
        .post(`/api/groups/${groupId}/folders`, { name, project_id: projectId })
        .then((response) => response.data);
    },
    /**
     * `source` tells the API which table owns the folder id. Master and project
     * folder ids come from separate sequences and overlap, so without it the API
     * has to guess and a project rename can land on a master folder.
     *
     * @param {'master'|'project'} source
     */
    rename(groupId, folderId, name, source = 'master', projectId = null) {
      return instance
        .put(`/api/groups/${groupId}/folders/${folderId}`, {
          name,
          source,
          project_id: projectId,
        })
        .then((response) => response.data);
    },
    delete(groupId, folderId, source = 'master', projectId = null) {
      return instance
        .delete(`/api/groups/${groupId}/folders/${folderId}`, {
          params: { source, project_id: projectId },
        })
        .then((response) => response.data);
    },
    reorder(groupId, orderedIds) {
      return instance
        .put(`/api/groups/${groupId}/folders/reorder`, { orderedIds })
        .then((response) => response.data);
    },
  },

  /** Project-scoped regulation uploads (do not affect master group) */
  projectRegulation: {
    create(projectId, uploadId, documentGroupId, regulationSubFolderId) {
      return instance
        .post(`/api/projects/${projectId}/regulation-uploads/${uploadId}`, {
          document_group_id: documentGroupId,
          regulation_sub_folder_id: regulationSubFolderId || null,
        })
        .then((response) => response.data);
    },
    index(projectId, documentGroupId) {
      return instance
        .get(`/api/projects/${projectId}/regulation-uploads`, {
          params: { document_group_id: documentGroupId },
        })
        .then((response) => response.data);
    },
    destroy(projectId, uploadId) {
      return instance
        .delete(`/api/projects/${projectId}/regulation-uploads/${uploadId}`)
        .then((response) => response.data);
    },
    sort(projectId, orderedUploadIds) {
      return instance
        .post(`/api/projects/${projectId}/regulation-uploads/sort`, {
          orderedUploadIds,
        })
        .then((response) => response.data);
    },
    reorderTopLevel(projectId, documentGroupId, items) {
      return instance
        .post(`/api/projects/${projectId}/regulations/reorder`, {
          document_group_id: documentGroupId,
          items,
        })
        .then((response) => response.data);
    },
  },
};

export { getDownloadUrl, uploadApi };
export default uploadApi;
